import React from "react";
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";

import { ResilienceReport } from "apps/company/api/rest/company-dashboard-resilience";
import { useTranslation } from "lib/i18n/use-translation";

import { K, Section, useDataThrough, useHalfLabel } from "./parts";
import {
  formatScore,
  trendAxis,
  trendValues,
} from "./resilience-view";
import styles from "./resilience.module.scss";

const WIDTH = 1000;
const HEIGHT = 280;

// Platform withholds the trend below this many halves.
const MINIMUM_POINTS = 2;

const LABEL = /^H([12]) (\d{4})$/;

// Platform labels each point in English ("H1 2026"); the page names halves
// by value and writes them in the viewer's language.
const pointValue = (label: string) => {
  const match = LABEL.exec(label);
  if (match === null) {
    throw new Error(`Not a half-year label: ${label}`);
  }
  return `${match[2]}-H${match[1]}`;
};

const Trend = ({ report }: { report: ResilienceReport }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const halfLabel = useHalfLabel();
  const dataThrough = useDataThrough();
  const trend = report.trend;

  if (!trend.suppressed && trend.points.length < MINIMUM_POINTS) {
    throw new Error("The trend came with fewer than two half-years");
  }
  // Before a second half there is no line to draw, so the card waits until
  // there is one.
  if (trend.suppressed) return null;
  const values = trendValues(trend.points);
  const plotted = values.filter((value) => value !== null).length;
  if (plotted < MINIMUM_POINTS) {
    return (
      <Section
        title={t(`${K}.sections.trendRepeat`)}
        eyebrow={t(`${K}.trend.eyebrow`)}
      >
        <p className={styles["card-description"]}>{t(`${K}.trend.tooFew`)}</p>
      </Section>
    );
  }

  // A hidden half is a gap in the line, never a zero.
  const data = trend.points.map((point, at) => ({
    half: halfLabel(pointValue(point.period)),
    index: values[at],
  }));
  const description = trend.points
    .map((point) =>
      t(`${K}.trend.point`, {
        half: halfLabel(pointValue(point.period)),
        index: point.index.suppressed
          ? t(`${K}.notEnoughData`)
          : formatScore(point.index.value, i18n.language),
      })
    )
    .join(" ");
  const axis = trendAxis(values.filter((value) => value !== null) as number[]);
  const latest = data.length - 1;

  return (
    <Section
      title={t(`${K}.sections.trendRepeat`)}
      info={t(`${K}.trend.info`)}
      eyebrow={t(`${K}.trend.eyebrow`)}
    >
      <p className={styles["card-description"]}>
        {t(`${K}.trend.description`)}
      </p>
      <figure className={styles["trend"]}>
        <div className={styles["trend-chart"]} aria-hidden="true">
          <LineChart
            width={WIDTH}
            height={HEIGHT}
            data={data}
            margin={{ top: 24, right: 24, bottom: 8, left: 0 }}
          >
            <CartesianGrid
              className={styles["trend-grid"]}
              strokeDasharray="2 4"
              vertical={false}
            />
            <XAxis
              dataKey="half"
              padding={{ left: 40, right: 40 }}
              tickLine={false}
              axisLine={false}
              className={styles["trend-axis"]}
            />
            <YAxis
              domain={axis.domain}
              ticks={axis.ticks}
              width={36}
              tickLine={false}
              axisLine={false}
              className={styles["trend-axis"]}
            />
            <Line
              dataKey="index"
              className={styles["trend-line"]}
              connectNulls={false}
              isAnimationActive={false}
              dot={({ cx, cy, index, key }) =>
                typeof cy !== "number" ? (
                  <g key={key} />
                ) : (
                  <circle
                    key={key}
                    cx={cx}
                    cy={cy}
                    r={index === latest ? 6 : 4}
                    className={
                      index === latest
                        ? styles["trend-dot-latest"]
                        : styles["trend-dot"]
                    }
                  />
                )
              }
            >
              <LabelList
                dataKey="index"
                position="top"
                className={styles["trend-label"]}
                formatter={(value: number) => formatScore(value, i18n.language)}
              />
            </Line>
          </LineChart>
        </div>
        <figcaption className={styles["visually-hidden"]}>
          {description}
        </figcaption>
      </figure>
      <p className={styles["card-footnote"]}>
        {dataThrough(report.period_value)}
      </p>
    </Section>
  );
};

export default Trend;
